import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mercapoli_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mercapoli_token');
      localStorage.removeItem('mercapoli_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Ride APIs
export const rideAPI = {
  create: (data) => api.post('/rides', data),
  getAll: () => api.get('/rides'),
  getById: (id) => api.get(`/rides/${id}`),
  updateStatus: (id, status) => api.patch(`/rides/${id}/status`, null, { params: { status } }),
  getAvailable: () => api.get('/rides/available/drivers'),
  accept: (id) => api.post(`/rides/${id}/accept`),
};

// Vehicle APIs
export const vehicleAPI = {
  create: (data) => api.post('/vehicles', data),
  getAll: (params) => api.get('/vehicles', { params }),
  getById: (id) => api.get(`/vehicles/${id}`),
  getMy: () => api.get('/vehicles/owner/my'),
};

// Rental APIs
export const rentalAPI = {
  create: (data) => api.post('/rentals', data),
  getAll: () => api.get('/rentals'),
  updateStatus: (id, status) => api.patch(`/rentals/${id}/status`, null, { params: { status } }),
};

// Delivery APIs
export const deliveryAPI = {
  create: (data) => api.post('/deliveries', data),
  getAll: () => api.get('/deliveries'),
  getById: (id) => api.get(`/deliveries/${id}`),
  updateStatus: (id, status) => api.patch(`/deliveries/${id}/status`, null, { params: { status } }),
  getAvailable: () => api.get('/deliveries/available/drivers'),
  accept: (id) => api.post(`/deliveries/${id}/accept`),
};

// Wallet APIs
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  topup: (data) => api.post('/wallet/topup', data),
  getTransactions: () => api.get('/wallet/transactions'),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  getExecutiveDashboard: (countryCode = null) => {
    const params = countryCode && countryCode !== 'GLOBAL' ? `?country_code=${countryCode}` : '';
    return api.get(`/admin/executive-dashboard${params}`);
  },
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  blockUser: (userId) => api.post(`/admin/users/${userId}/block`),
  unblockUser: (userId) => api.post(`/admin/users/${userId}/unblock`),
};

// Config APIs
export const configAPI = {
  getCountries: () => api.get('/config/countries'),
  getVehicleTypes: () => api.get('/config/vehicle-types'),
  getPaymentMethods: () => api.get('/config/payment-methods'),
};

// Stripe Payment APIs
export const stripeAPI = {
  createCheckout: (data) => api.post('/payments/stripe/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
};

// Referral APIs
export const referralAPI = {
  getCode: () => api.get('/referral/code'),
  getStats: () => api.get('/referral/stats'),
};

// Digi Wallet APIs
export const digiWalletAPI = {
  link: (email) => api.post('/wallet/link-digi', { email }),
  getBalance: () => api.get('/wallet/digi-balance'),
};

// Emergency APIs
export const emergencyAPI = {
  // User actions
  createAlert: (data) => api.post('/emergency/alert', data),
  updateLocation: (alertId, location) => api.post(`/emergency/location-update?alert_id=${alertId}`, location),
  
  // Admin actions
  getAlerts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.country_code) queryParams.append('country_code', params.country_code);
    const query = queryParams.toString();
    return api.get(`/admin/emergency-alerts${query ? '?' + query : ''}`);
  },
  getAlertDetail: (alertId) => api.get(`/admin/emergency-alerts/${alertId}`),
  acknowledgeAlert: (alertId) => api.post(`/admin/emergency-alerts/${alertId}/acknowledge`),
  addNote: (alertId, note, severityChange = null) => api.post(`/admin/emergency-alerts/${alertId}/note`, { 
    note, 
    severity_change: severityChange 
  }),
  escalateAlert: (alertId) => api.post(`/admin/emergency-alerts/${alertId}/escalate`),
  resolveAlert: (alertId, resolutionNotes, resolutionType) => api.post(`/admin/emergency-alerts/${alertId}/resolve`, {
    resolution_notes: resolutionNotes,
    resolution_type: resolutionType
  }),
  getStats: () => api.get('/admin/emergency-stats'),
  getSecurityKPIs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.country_code) queryParams.append('country_code', params.country_code);
    if (params.period_days) queryParams.append('period_days', params.period_days);
    const query = queryParams.toString();
    return api.get(`/admin/security-kpis${query ? '?' + query : ''}`);
  },
};

// User Role APIs
export const userRoleAPI = {
  getRoles: () => api.get('/user/roles'),
  activateRole: (role, additionalData) => api.post('/user/activate-role', { role, additional_data: additionalData }),
};

// Admin Country APIs
export const adminCountryAPI = {
  getAll: () => api.get('/admin/countries'),
  create: (data) => api.post('/admin/countries', data),
  update: (code, data) => api.put(`/admin/countries/${code}`, data),
  delete: (code) => api.delete(`/admin/countries/${code}`),
};

// Ratings & Reviews APIs
export const ratingsAPI = {
  create: (data) => api.post('/ratings', data),
  getServiceRatings: (serviceType, serviceId) => api.get(`/ratings/service/${serviceType}/${serviceId}`),
  getUserRatings: (userId) => api.get(`/ratings/user/${userId}`),
  getPending: () => api.get('/ratings/my-pending'),
  adminGetAll: () => api.get('/admin/ratings'),
  adminDelete: (ratingId) => api.delete(`/admin/ratings/${ratingId}`),
};

// Push Notifications APIs
export const notificationsAPI = {
  registerToken: (token, deviceType = 'web') => 
    api.post('/notifications/register-token', { token, device_type: deviceType }),
  unregisterToken: (token) => 
    api.delete('/notifications/unregister-token', { data: { token } }),
  getAll: (limit = 20, unreadOnly = false) => 
    api.get(`/notifications?limit=${limit}&unread_only=${unreadOnly}`),
  markAsRead: (notificationId) => 
    api.patch(`/notifications/${notificationId}/read`),
  markAllAsRead: () => 
    api.patch('/notifications/read-all'),
  getStatus: () => 
    api.get('/notifications/status'),
  adminSend: (data) => 
    api.post('/admin/notifications/send', data),
};

// Support Ticket APIs (for users)
export const supportAPI = {
  createTicket: (data) => api.post('/support/ticket', data),
  getMyTickets: () => api.get('/support/tickets'),
};

// Operator APIs (for Central de Monitoreo)
export const operatorAPI = {
  // Auth
  login: (data) => api.post('/operator/login', data),
  // Dashboard
  getDashboard: () => api.get('/operator/dashboard'),
  // Emergencies
  getEmergencies: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/operator/emergencies${query ? '?' + query : ''}`);
  },
  getEmergencyDetail: (alertId) => api.get(`/operator/emergency/${alertId}`),
  takeEmergency: (alertId) => api.post(`/operator/emergency/${alertId}/take`),
  // Support Tickets
  getSupportTickets: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/operator/support/tickets${query ? '?' + query : ''}`);
  },
  takeSupportTicket: (ticketId) => api.post(`/operator/support/ticket/${ticketId}/take`),
  replyToTicket: (ticketId, message) => api.post(`/operator/support/ticket/${ticketId}/reply`, null, { params: { message } }),
  resolveTicket: (ticketId, notes) => api.post(`/operator/support/ticket/${ticketId}/resolve`, null, { params: { resolution_notes: notes } }),
  // Active Services
  getActiveServices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/operator/services/active${query ? '?' + query : ''}`);
  },
  // WhatsApp
  getWhatsAppLink: (userId, context = 'general', alertId = null) => {
    const params = new URLSearchParams({ context });
    if (alertId) params.append('alert_id', alertId);
    return api.get(`/operator/whatsapp-link/${userId}?${params.toString()}`);
  },
};

// Risk Score API
export const riskScoreAPI = {
  get: (userId) => api.get(`/risk-score/${userId}`),
};

// Chat APIs
export const chatAPI = {
  getMessages: (alertId, limit = 100) => api.get(`/chat/${alertId}/messages?limit=${limit}`),
  sendMessage: (alertId, content) => api.post(`/chat/${alertId}/message`, { content }),
};

export default api;
